import { INSURANCE_TYPES_NAMES } from 'constants/insurance-types-names';
import { TASK_TYPES } from 'constants/notification';
import {
  MARKET_SCAN_COMPARE_POLICY_ROUTE,
  MARKET_SCAN_RESULTS_ROUTE,
  PROFILE_SHARE_ROUTE,
} from 'constants/routes';
import {
  getFieldByPolicyType,
  getMarketScanEndScreenRoute,
  getMarketScanLifeResultsRoute,
  getMarketScanResultsRoute,
  getPolicyDetailsRoute,
  routeReplaceId,
} from 'helpers';
import history from 'helpers/history';
import React from 'react';
import { IAppState, INotification, IPolicyScan } from 'types';
import styles from './styles/NotificationList.module.scss';
import { ProviderNames } from 'constants/policy';
import {
  redirectToLifeMarketScanResults,
  redirectToMbiMarketScanResults,
} from 'helpers/notification';

const getFieldValue = (data, policyType) => {
  const insuranceField = getFieldByPolicyType(policyType);
  const value = data?.[insuranceField?.fieldKey];

  if (
    (policyType === INSURANCE_TYPES_NAMES.MOTORCYCLE ||
      policyType === INSURANCE_TYPES_NAMES.VEHICLE ||
      policyType === INSURANCE_TYPES_NAMES.RV) &&
    !!value
  ) {
    return value.toUpperCase();
  }

  if (policyType === INSURANCE_TYPES_NAMES.LIFE && data) {
    return `Life Insurance`;
  }

  return value;
};

const getMsDescription = (
  policyScans: IPolicyScan[],
  { target, policyType }: INotification,
) => {
  const scan = getById(policyScans, target);
  if (policyType === INSURANCE_TYPES_NAMES.LIFE) {
    return 'Life Insurance';
  }
  return getFieldValue(scan, policyType) || policyType;
};

const getById = (items, id) => {
  return items.find(R.propEq('id', id));
};

export const handleNotificationClick = (
  notification: INotification,
  dispatch,
  appState: IAppState,
) => {
  switch (notification.notificationType) {
    case TASK_TYPES.ACTIVE_SHARE_POLICIES:
    case TASK_TYPES.PENDING_SHARE_POLICIES: {
      history.push(PROFILE_SHARE_ROUTE);
      break;
    }
    case TASK_TYPES.POLICY_SCAN_PENDING:
    case TASK_TYPES.POLICY_SCAN_IN_REVIEW: {
      if (notification.policyType === INSURANCE_TYPES_NAMES.LIFE) {
        redirectToLifeMarketScanResults({
          id: notification.target,
          sk: notification.policyId,
          createdAt: notification.createdAt,
        });
      } else if (notification.policyType === INSURANCE_TYPES_NAMES.MBI) {
        redirectToMbiMarketScanResults({
          id: notification.target,
          sk: notification.policyId,
          createdAt: notification.createdAt,
        });
      } else {
        const item = getById(
          appState.policies.policyScans,
          notification.target,
        );
        if (item) {
          history.push(getMarketScanEndScreenRoute(item.id));
        }
      }

      break;
    }
    case TASK_TYPES.POLICY_SCAN_CONTINUE: {
      const item = getById(appState.policies.policies, notification.target);
      if (item) {
        history.push(routeReplaceId(MARKET_SCAN_COMPARE_POLICY_ROUTE, item.id));
      }
      break;
    }
    case TASK_TYPES.POLICY_SCAN_READY: {
      if (notification.policyType === INSURANCE_TYPES_NAMES.LIFE) {
        history.push(
          getMarketScanLifeResultsRoute(
            notification.target,
            notification.policyId,
          ),
        );
      } else if (notification.policyType === INSURANCE_TYPES_NAMES.MBI) {
        redirectToMbiMarketScanResults({
          id: notification.target,
          sk: notification.policyId,
          createdAt: notification.createdAt,
        });
      } else {
        history.push(
          getMarketScanResultsRoute(notification.target, notification.policyId),
        );
      }
      break;
    }
    case TASK_TYPES.POLICY_SCAN_PROTECTA_REFERRAL:
    case TASK_TYPES.POLICY_SCAN_PURCHASE_PROGRESS: {
      if (
        notification.policyProviderName === ProviderNames.MAS &&
        notification.policyType === INSURANCE_TYPES_NAMES.LIFE
      ) {
        history.push(
          `${getMarketScanResultsRoute(notification.target, notification.policyId)}&openCallback=1&callbackFlow=life`,
        );
        break;
      }
      const item = getById(appState.policies.policyScans, notification.target);
      if (item) {
        history.push(
          `${MARKET_SCAN_RESULTS_ROUTE}?id=${item.id}&sk=${item.sk}`,
        );
      }
      break;
    }
    case TASK_TYPES.POLICY_SCAN_CALLBACK_SUCCESS: {
      const lifeParams =
        notification.policyType === INSURANCE_TYPES_NAMES.LIFE
          ? '&openCallback=1&callbackFlow=life'
          : '';
      history.push(
        `${getMarketScanResultsRoute(notification.target, notification.policyId)}${lifeParams}`,
      );
      break;
    }

    case TASK_TYPES.POLICY_SCAN_PURCHASE_COMPLETED:
    case TASK_TYPES.POLICY_RENEWAL:
    case TASK_TYPES.POLICY_EXPIRED: {
      const route = getPolicyDetailsRoute(notification.target);
      history.push(route);
      break;
    }
    default:
      break;
  }
};

export const getDotClassNameByType = (taskType: TASK_TYPES) => {
  switch (taskType) {
    case TASK_TYPES.POLICY_SCAN_IN_REVIEW:
      return styles.yellowDot;
    case TASK_TYPES.POLICY_SCAN_PENDING:
      return styles.blinkDot;
    case TASK_TYPES.POLICY_SCAN_READY:
    case TASK_TYPES.POLICY_SCAN_CALLBACK_SUCCESS:
      return styles.greenDot;
    default:
      return '';
  }
};

export const getText = (notification: INotification, appState: IAppState) => {
  switch (notification.notificationType) {
    case TASK_TYPES.POLICY_EXPIRED: {
      const p = getById(appState.policies.policies, notification.target);
      const title = 'Policy';
      const subtitle = 'Renew now';
      const description = getFieldValue(p, notification.policyType);
      return {
        title,
        subtitle,
        description,
      };
    }
    case TASK_TYPES.POLICY_RENEWAL: {
      const p = getById(appState.policies.policies, notification.target);
      const title = 'Policy';
      const subtitle = 'Expiring soon';
      const description = getFieldValue(p, notification.policyType);
      return {
        title,
        subtitle,
        description,
      };
    }
    case TASK_TYPES.POLICY_SCAN_PURCHASE_COMPLETED: {
      const p = getById(appState.policies.policies, notification.target);
      const title = 'Policy';
      const subtitle = 'Purchase completed';
      const description = getFieldValue(p, notification.policyType);
      return {
        title,
        subtitle,
        description,
      };
    }
    case TASK_TYPES.POLICY_SCAN_PENDING: {
      const title = 'Market Scan';
      const subtitle = 'Scanning';
      const description = getMsDescription(
        appState.policies.policyScans,
        notification,
      );
      return {
        title,
        subtitle,
        description,
      };
    }
    case TASK_TYPES.POLICY_SCAN_IN_REVIEW: {
      const title = 'Market Scan';
      const subtitle = 'In review';
      const description = 'We’re looking into it.';
      return {
        title,
        subtitle,
        description,
      };
    }
    case TASK_TYPES.POLICY_SCAN_CONTINUE: {
      const title = 'Market Scan';
      const subtitle = 'Continue now';
      const description = getMsDescription(
        appState.policies.policyScans,
        notification,
      );
      return {
        title,
        subtitle,
        description,
      };
    }
    case TASK_TYPES.POLICY_SCAN_READY: {
      const title = 'Market Scan';
      const subtitle = 'Ready';
      const description = getMsDescription(
        appState.policies.policyScans,
        notification,
      );
      return {
        title,
        subtitle,
        description,
      };
    }
    case TASK_TYPES.POLICY_SCAN_PROTECTA_REFERRAL: {
      const scan = getById(appState.policies.policyScans, notification.target);
      const providerName = notification.policyProviderName;
      let title;

      if (providerName === ProviderNames.STAR) {
        title = 'Purchase Star Insure';
      } else {
        title = 'Purchase Assurant';
      }
      const description = scan?.referral?.policyId
        ? `Referral ID ${scan.referral?.policyId}`
        : '';
      return {
        title,
        description,
      };
    }
    case TASK_TYPES.POLICY_SCAN_PURCHASE_PROGRESS: {
      const providerName = notification.policyProviderName;
      let title = ""

      if (
        providerName === ProviderNames.MAS &&
        notification.policyType === INSURANCE_TYPES_NAMES.LIFE
      ) {
        return {
          title: 'MAS Life Insurance',
          description: 'Request quote now',
        };
      }

      if (providerName === ProviderNames.MAS) {
        title = 'Purchase MAS';
      } else if (providerName === ProviderNames.STAR) {
        title = 'Purchase Star Insure';
      } else if (providerName === ProviderNames.PROVIDENT) {
        title = 'Purchase Provident';
      } else {
        title = 'Purchase Assurant';
      }

      const description = 'Finish now';
      return {
        title,
        description,
      };
    }
    case TASK_TYPES.POLICY_SCAN_PURCHASE_PROGRESS_MBI: {
      const title = 'Mechanical Breakdown Purchase';
      const description = 'Finish now';
      return {
        title,
        description,
      };
    }
    case TASK_TYPES.POLICY_SCAN_CALLBACK_SUCCESS: {
      // MAS callbacks carry no provider (title defaulted to MAS); survey-campaign
      // callbacks set policyProviderName to the partner asked about.
      const provider = notification.policyProviderName || 'MAS';
      const title =
        notification.policyType === INSURANCE_TYPES_NAMES.LIFE
          ? `${provider} Life`
          : `${provider} ${notification.policyType}`;
      return {
        title,
        description: 'Callback request success',
      };
    }
    case TASK_TYPES.PENDING_SHARE_POLICIES: {
      const title = 'Shared Dashboard';
      const subtitle = `Invited`;
      const description = notification.inviteeName;
      return {
        title,
        subtitle,
        description,
      };
    }
    case TASK_TYPES.ACTIVE_SHARE_POLICIES: {
      const title = `Shared Dashboard`;
      const subtitle = `Active`;
      const description = notification.inviteeName;
      return {
        title,
        subtitle,
        description,
      };
    }
    case TASK_TYPES.COMPLETE_BROKER_FORM: {
      const title = `Complete questions for more quotes`;
      const description = getMsDescription(
        appState.policies.policyScans,
        notification,
      );
      return {
        title,
        description,
      };
    }
    default:
      return {
        title: '',
        description: '',
      };
  }
};


export const removeDuplicateNotifications = (items) => {
  return Array.from(new Set(items.map((item) => item.id))).map((id) =>
    items.find((item) => item.id === id),
  );
};
